import { Star, BadgeCheck, Quote } from 'lucide-react';
import { REVIEWS, RATING_SUMMARY, type Review } from '../data/reviews';
import { SectionHead } from './Bits';
import { cn } from '../lib/util';

function Stars({ n }: { n: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={15} fill={i < n ? 'var(--color-gold)' : 'transparent'} style={{ color: 'var(--color-gold)' }} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function Card({ r }: { r: Review }) {
  return (
    <article
      className="card shrink-0 w-[300px] sm:w-[360px] p-6 flex flex-col"
      style={{ boxShadow: 'var(--pe-shadow-sm)' }}
    >
      <div className="flex items-center justify-between">
        <Stars n={r.rating} />
        <Quote size={22} style={{ color: 'var(--color-accent-soft)' }} fill="var(--color-accent-soft)" />
      </div>
      <p className="mt-4 flex-1 text-ink-2" style={{ fontSize: '1rem', lineHeight: 1.6 }}>
        “{r.quote}”
      </p>
      <div className="mt-5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-ink">{r.author}</span>
            {r.verified && <BadgeCheck size={15} style={{ color: 'var(--color-leaf)' }} aria-label="Verified review" />}
          </div>
          <div className="text-[0.82rem] text-ink">{r.meta}</div>
        </div>
        <span className="text-[0.72rem] font-semibold tracking-wide rounded-full px-2.5 py-1" style={{ background: 'var(--color-card-2)', color: 'var(--color-muted)' }}>
          {r.source}
        </span>
      </div>
    </article>
  );
}

function Row({ items, reverse = false, duration }: { items: Review[]; reverse?: boolean; duration: number }) {
  const loop = [...items, ...items];
  return (
    <div className="marquee relative overflow-hidden py-2" aria-hidden={reverse}>
      <div
        className="marquee-track gap-5"
        style={{
          animation: `marquee ${duration}s linear infinite`,
          animationDirection: reverse ? 'reverse' : 'normal',
        }}
      >
        {loop.map((r, i) => (
          <Card key={`${r.author}-${i}`} r={r} />
        ))}
      </div>
    </div>
  );
}

export function Reviews() {
  const half = Math.ceil(REVIEWS.length / 2);
  const rowA = REVIEWS.slice(0, half);
  const rowB = REVIEWS.slice(half);

  return (
    <section className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--color-surface-2)' }}>
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead kicker="Kind Words" title="Loved across Coppell" intro={RATING_SUMMARY.detail} />
          <div className="flex items-center gap-3">
            <div className="flex -space-x-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={20} fill="var(--color-gold)" style={{ color: 'var(--color-gold)' }} />
              ))}
            </div>
            <span className="font-display text-lg text-ink">Real reviews</span>
          </div>
        </div>
      </div>

      {/* Edge fade masks */}
      <div className="relative mt-12" style={{ WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)', maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)' }}>
        <div className={cn('flex flex-col gap-5')}>
          <Row items={rowA} duration={46} />
          <Row items={rowB} reverse duration={54} />
        </div>
      </div>
    </section>
  );
}
